import './RouteTile.scss'

import React, { useState } from 'react'

import { RouteData } from '../RouteData'
import { FavoriteInfo } from '../FavoriteInfo'
import { RouteThumbnail } from '../RouteThumbnail'
import { User } from '../User'
import { Tag } from '../Tag'
import { translated } from '../_translation'

/**
 * @typedef {Object} RouteTileProps
 * @property {import('../../js/entities').Route} [route]
 * @property {import('../../js/entities').User} [user] The user (owner) of the route
 * @property {(status: boolean) => void} [onFavorite] Handler for when the route is marked as favorite
 * @property {string} [className] Additional classes
 * @property {(e: React.MouseEvent) => void} [onMouseEnter]
 * @property {(e: React.MouseEvent) => void} [onMouseLeave]
 * @property {() => string} [getRouteUrl]
 */

/**
 * Tile for use in grids that represents a route.
 * @param {RouteTileProps} props
 */
const RouteTile = ({ gettext, route, user, onFavorite, className, onMouseEnter, onMouseLeave, getRouteUrl }) => {
    const [routeUrl, setRouteUrl] = useState('#')

    if (!route) return <div className="route-tile-placeholder" data-testid="placeholder" />

    const tagNames = route.getCategoryNames().concat(route.getGroundNames())
    const handleFavoriteClick = onFavorite ? () => onFavorite(!route.isFavorite) : null
    const classes = 'route-tile' + (className ? ' ' + className : '')

    const imgAltText = gettext('Route image') + ' '

    const handleMouseEnter = e => {
        setRouteUrl(getRouteUrl())
        if (onMouseEnter) {
            onMouseEnter(e)
        }
    }

    return (
        <div className={classes}>
            <div className="tile-media">
                <RouteThumbnail image={route.staticmap} alt={route.title} />
                <div className="route-tile-images">
                    { route.images.slice(0, 3).map((url, i) => (
                        <img src={url} alt={imgAltText + (i + 1)} key={i} width={75} height={50} loading='lazy' />
                    )) }
                </div>
                <div className="route-tile-tags">
                    { tagNames.map((tagName, i) => <Tag key={i}>{tagName}</Tag>) }
                </div>
                {user && <User user={user} info={route.created.fromNow()} className="layout-dark" />}
            </div>
            <div className="route-tile-info">
                <FavoriteInfo isFavorite={route.isFavorite} favoriteCount={route.favoriteCount}
                    onClick={handleFavoriteClick} />
                <div className="tile-title">
                    <a className="tile-link" href={routeUrl} onMouseEnter={handleMouseEnter}
                        onMouseLeave={onMouseLeave}>
                        {route.title}
                    </a>
                </div>
                <RouteData distance={route.distance} ascent={route.ascent} descent={route.descent} />
                <p className="route-tile-location">
                    <i className="i-location text-secondary i-sm" /> {route.location}
                </p>
            </div>
        </div>
    )
}

export default translated(RouteTile)
